import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// 导入现有的 messages JSON 文件（相对于 apps/web/src/，需要 ../）
import enCommon from '../messages/en/common.json';
import enHome from '../messages/en/home.json';
import enAuth from '../messages/en/auth.json';
import enMarketplace from '../messages/en/marketplace.json';
import enAgents from '../messages/en/agents.json';
import enSession from '../messages/en/session.json';
import enDashboard from '../messages/en/dashboard.json';
import enDocs from '../messages/en/docs.json';
import enAdmin from '../messages/en/admin.json';
import enCommonActions from '../messages/en/common.actions.json';
import enOrders from '../messages/en/orders.json';
import enFavorites from '../messages/en/favorites.json';
import enDashboardProvider from '../messages/en/dashboard.provider.json';
import enTerms from '../messages/en/terms.json';
import enPrivacy from '../messages/en/privacy.json';
import enAbout from '../messages/en/about.json';
import enRefundPolicy from '../messages/en/refundPolicy.json';
import enLegal from '../messages/en/legal.json';
import enSupport from '../messages/en/support.json';
import enPromo from '../messages/en/promo.json';
import enMecha from '../messages/en/mecha.json';

import zhCnCommon from '../messages/zh-CN/common.json';
import zhCnHome from '../messages/zh-CN/home.json';
import zhCnAuth from '../messages/zh-CN/auth.json';
import zhCnMarketplace from '../messages/zh-CN/marketplace.json';
import zhCnAgents from '../messages/zh-CN/agents.json';
import zhCnSession from '../messages/zh-CN/session.json';
import zhCnDashboard from '../messages/zh-CN/dashboard.json';
import zhCnDocs from '../messages/zh-CN/docs.json';
import zhCnAdmin from '../messages/zh-CN/admin.json';
import zhCnCommonActions from '../messages/zh-CN/common.actions.json';
import zhCnOrders from '../messages/zh-CN/orders.json';
import zhCnFavorites from '../messages/zh-CN/favorites.json';
import zhCnDashboardProvider from '../messages/zh-CN/dashboard.provider.json';
import zhCnTerms from '../messages/zh-CN/terms.json';
import zhCnPrivacy from '../messages/zh-CN/privacy.json';
import zhCnAbout from '../messages/zh-CN/about.json';
import zhCnRefundPolicy from '../messages/zh-CN/refundPolicy.json';
import zhCnLegal from '../messages/zh-CN/legal.json';
import zhCnSupport from '../messages/zh-CN/support.json';
import zhCnPromo from '../messages/zh-CN/promo.json';
import zhCnMecha from '../messages/zh-CN/mecha.json';

const resources = {
  en: {
    common: enCommon,
    home: enHome,
    auth: enAuth,
    marketplace: enMarketplace,
    agents: enAgents,
    session: enSession,
    dashboard: enDashboard,
    docs: enDocs,
    admin: enAdmin,
    'common.actions': enCommonActions,
    orders: enOrders,
    favorites: enFavorites,
    'dashboard.provider': enDashboardProvider,
    terms: enTerms,
    privacy: enPrivacy,
    about: enAbout,
    refundPolicy: enRefundPolicy,
    legal: enLegal,
    support: enSupport,
    promo: enPromo,
    mecha: enMecha,
  },
  'zh-CN': {
    common: zhCnCommon,
    home: zhCnHome,
    auth: zhCnAuth,
    marketplace: zhCnMarketplace,
    agents: zhCnAgents,
    session: zhCnSession,
    dashboard: zhCnDashboard,
    docs: zhCnDocs,
    admin: zhCnAdmin,
    'common.actions': zhCnCommonActions,
    orders: zhCnOrders,
    favorites: zhCnFavorites,
    'dashboard.provider': zhCnDashboardProvider,
    terms: zhCnTerms,
    privacy: zhCnPrivacy,
    about: zhCnAbout,
    refundPolicy: zhCnRefundPolicy,
    legal: zhCnLegal,
    support: zhCnSupport,
    promo: zhCnPromo,
    mecha: zhCnMecha,
  },
};

// 同步从 URL 路径提取语言，避免 LanguageDetector 异步检测导致的首次渲染闪烁
function getLocaleFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(/^\/([a-z]{2}(-[A-Z]{2})?)/i);
  return match?.[1];
}

const urlLocale = getLocaleFromPath();

// i18n.language 会作为原始值发往 OAuth start 等接口，必须白名单化：
// 非法路径（如无 locale 前缀的 /login 会截出 'lo'）不允许成为 i18n.language
const SUPPORTED_LOCALES = ['en', 'zh-CN'] as const;
const safeLocale = urlLocale && (SUPPORTED_LOCALES as readonly string[]).includes(urlLocale) ? urlLocale : 'en';

i18n
  .use(LanguageDetector)
  .init({
    resources,
    lng: safeLocale, // 优先使用 URL 中的语言（仅限白名单），避免首次渲染用错语言/发错 locale
    fallbackLng: 'en',
    supportedLngs: ['en', 'zh-CN'],

    // 调试模式（开发时开启）
    debug: false,

    // 命名空间配置
    ns: [
      'common', 'home', 'auth', 'marketplace', 'agents', 'session',
      'dashboard', 'docs', 'admin', 'common.actions', 'orders', 'favorites',
      'dashboard.provider', 'terms', 'privacy', 'about', 'refundPolicy', 'support', 'promo',
      'mecha', 'legal',
    ],
    defaultNS: 'common',

    // 支持嵌套 key（admin.agents.title 这种写法）
    keySeparator: '.',
    nsSeparator: ':',

    // t() 缺失返回 key 字面量（string），而非 null —— 避免 input value/参数等处的 string|null 类型问题
    returnNull: false,

    // 插值配置
    // 全站 messages JSON 统一使用单花括号 {var}（非 i18next 默认的 {{var}}），
    // 因此显式指定 prefix/suffix，否则所有插值会原样显示成字面量。
    interpolation: {
      escapeValue: false, // React 已经防 XSS
      prefix: '{',
      suffix: '}',
    },

    // 检测配置
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
    },
  });

// 同步 UI 语言到后端 user.locale（供通知 i18n 渲染——#5）
i18n.on('languageChanged', (lng) => {
  try {
    const stored = JSON.parse(localStorage.getItem('clawrent-auth') ?? '{}');
    const token = stored?.state?.token;
    if (token) {
      fetch('/api/users/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ locale: lng }),
      }).catch(() => {});
    }
  } catch { /* silent */ }
});

export default i18n;
